import { useQuery } from "@apollo/client";
import { DynamicValue } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties } from "react";
import { Badge, Button } from "react-bootstrap";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getFilterSelectorTranslation from "src/functions/getFilterSelectorTranslation";
import Cancel from "src/icons/Cancel";
import Filter from "src/icons/Filter";
import Small_cancel from "src/icons/Small-cancel";
import { profileNames } from "src/queries/profileNames";
import { sBadge } from "src/styles/sBadge.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sFilter } from "src/styles/sFilter.module.scss";

export const difficulties = [
  "beginner",
  "intermediate",
  "advanced",
  "supreme",
] as const;

// export const durations = ["short", "medium", "long", "any"];

export const focusTypes = [
  "any",
  "balance",
  "breathing",
  "communication",
  "concentration",
  "development",
  "enlightenment",
  "flexibility",
  "flow",
  "health",
  "knowledge",
  "meditation",
  "mobility",
  "morality",
  "movement",
  "postures",
  "realization",
  "regeneration",
  "relaxation",
  "senses",
  "slowdown",
  "stamina_in_postures",
  "stimulation",
  "strengthening",
  "stretching",
] as const;

export const recommendedFocusTypes = [
  "breathing",
  "concentration",
  "development",
  "enlightenment",
  "flexibility",
] as const;

export const skills = [
  "being_juvenility",
  "being_knowledge",
  "being_non_greed",
  "being_non_violence",
  "being_purity",
  "being_renunciation",
  "being_resignation",
  "being_restraint",
  "being_satisfaction",
  "being_self_acceptance",
  "being_self_awareness",
  "being_truthfulness",
  "being_vigilance",
  "body_balance",
  "body_bend",
  "body_blood_supply",
  "body_bow_aside",
  "body_core_strengthening",
  "body_correct_posture",
  "body_fertility_support",
  "body_firming",
  "body_flexibility",
  "body_flow",
  "body_forward_bend",
  "body_handling",
  "body_healthy_feet",
  "body_hips_opening",
  "body_hormonal_balance",
  "body_chest_opening",
  "body_inclination",
  "body_internal_muscles_strengthening",
  "body_motor_activity",
  "body_movement",
  "body_organism_activation",
  "body_physical_endurance",
  "body_relaxation",
  "body_relaxed_mimic_muscles",
  "body_spine_flexibility",
  "body_stability",
  "body_stretching",
  "body_strengthening",
  "body_symmetry_in_motion",
  "body_torsion",
  "body_warming",
  "mind_body_connection",
  "mind_borders_perception",
  "mind_brain_function",
  "mind_calm",
  "mind_communication",
  "mind_concentration",
  "mind_creative_thinking",
  "mind_determination",
  "mind_greater_performance",
  "mind_improving_cognitive_functions",
  "mind_logical_thinking",
  "mind_purposefulness",
  "mind_relaxation",
  "mind_self_confidence",
  "mind_space_orientation",
  "mind_tension_release",
  "soul_good_mood",
  "soul_psychological_resilience",
  "soul_senses_sharpening",
  "soul_serenity",
  "soul_stress_resistance",
  "technique_breath_ujjayi",
  "technique_full_yoga_breath",
  "technique_handstand",
  "technique_headstand",
  "technique_massage",
  "technique_nidra_yoga",
  "technique_scarfing",
  "technique_shavasana",
  "technique_split",
  "vitality_breath_slowing",
  "vitality_breath_work",
  "vitality_deep_relaxation",
  "vitality_emotion_control",
  "vitality_feel_confident",
  "vitality_female_cycle",
  "vitality_heartfelt",
  "vitality_increase_libido",
  "vitality_life_energy",
  "vitality_lungs_vital_capacity",
] as const;

export const recommendedSkills = [
  "being_juvenility",
  "being_knowledge",
  "being_non_greed",
  "being_non_violence",
  "being_purity",
] as const;

export const bodyParts = [
  "abs",
  "ankles",
  "anterior_body",
  "arms",
  "back",
  "brain",
  "calves",
  "cardiovascular_system",
  "chest",
  "core",
  "eyes",
  "face",
  "fascia",
  "feet",
  "full_body",
  "glutes",
  "hamstrings",
  "hands",
  "head",
  "heart",
  "hips",
  "immune_system",
  "intestines",
  "it_band",
  "kidneys",
  "knees",
  "legs",
  "liver",
  "lower_back",
  "lungs",
  "neck",
  "nervous_system",
  "obliques",
  "pancreas",
  "pelvic_floor",
  "posterior_body",
  "psoas",
  "quadriceps",
  "respiratory_system",
  "shoulder_blades",
  "shoulders",
  "side_body",
  "spine",
  "stomach",
  "thighs",
  "upper_back",
  "wrists",
] as const;

export const recommendedBodyParts = [
  "arms",
  "legs",
  "hamstrings",
  "back",
  "calves",
] as const;

export const styleTypes = [
  "any",
  "acro",
  "anusara",
  "ashtanga",
  "baby",
  "barrare",
  "bikram",
  "DNS_method",
  "face",
  "fly",
  "for_kids",
  "hatha",
  "group_exercise",
  "chakra",
  "iyengar",
  "jivamukti",
  "kundalini",
  "laughter_yoga",
  "massage",
  "meditation",
  "mindfulness",
  "more_styles",
  "movement",
  "mysore",
  "office_yoga",
  "paddleboard",
  "pilates",
  "postnatal",
  "power",
  "prana_flow",
  "pranayama",
  "prenatal",
  "restorative",
  "rocket",
  "rolf",
  "scarfing",
  "tantra",
  "teenage",
  "therapeutic",
  "traditional",
  "vinyasa",
  "wheel",
  "yin",
  "yoga",
] as const;

export const recommendedStyleTypes = [
  "hatha",
  "iyengar",
  "kundalini",
  "ashtanga",
  "vinyasa",
] as const;

export const equipments = [
  "ankle_weights",
  "block",
  "blanket",
  "bolster",
  "dumbbells",
  "heated_pillow",
  "eye_pillow",
  "fit_ball",
  "foam_roller",
  "gliding_discs",
  "chair",
  "magic_circle",
  "no_props",
  "overball",
  "resistance_band",
  "resistance_loop",
  "scarf",
  "small_stability_ball",
  "strap",
  "therapy_ball",
  "towel",
  "yoga_mat",
  "wall_space",
  "weights",
];

export const recommendedEquipments = [
  "ankle_weights",
  "block",
  "blanket",
  "bolster",
  "heated_pillow",
] as const;

export const languages = ["english", "czech", "slovak"];

export type Difficulties = (typeof difficulties)[number];
export type FocusType = (typeof focusTypes)[number];
export type Skill = (typeof skills)[number];
export type BodyPart = (typeof bodyParts)[number];
export type StyleType = (typeof styleTypes)[number];
export type Equipment = (typeof equipments)[number];
// export type Duration = (typeof durations)[number];
export type Language = (typeof languages)[number];

export type FiltersState = {
  sort: string;
  paid: boolean;
  subscription: boolean;
  subscription_allowed: boolean;
  favourite: boolean;
  rating: number;
  difficulty: Set<Difficulties>;
  focusType: Set<FocusType>;
  skills: Set<Skill>;
  bodyPart: Set<BodyPart>;
  styleType: Set<StyleType>;
  equipment: Set<Equipment>;
  //  duration: Set<Duration>;
  instructor: Set<string>;
  language: Set<Language>;
  page: number;
};

export const defaultFilter: FiltersState = {
  // sort: "created_at:desc", //TODO: when graphql verified is ready
  sort: "rating:desc",
  paid: false,
  subscription: false,
  subscription_allowed: false,
  favourite: false,
  rating: 0,
  difficulty: new Set(),
  focusType: new Set(),
  skills: new Set(),
  bodyPart: new Set(),
  styleType: new Set(),
  equipment: new Set(),
  //  duration: new Set(),
  instructor: new Set(),
  language: new Set(),
  page: 1,
};

export function filtersToQuery(filters: FiltersState): {
  [key: string]: string;
} {
  return Object.fromEntries(
    Object.entries(filters)
      .map(([key, val]) => {
        if (val instanceof Set) {
          if (!val.size) {
            return null;
          }
          return [key, Array.from(val).join("~")];
        } else {
          if (!val) {
            return null;
          }
          if (key === "page" && val === defaultFilter.page) {
            return null;
          }
          if (key === "sort" && val === defaultFilter.sort) {
            return null;
          }
          return [key, String(val)];
        }
      })
      .filter(Boolean)
  );
}

export function queryToFilters(
  query: {
    [key: string]: string | string[];
  },
  defaultFilterValues: Partial<FiltersState>
): FiltersState {
  const newFilters: any = Object.fromEntries(
    Object.entries({ ...defaultFilter, ...defaultFilterValues }).map(
      ([key, defaultVal]) => {
        const queryVal = query[key];
        if (typeof queryVal === "string") {
          if (defaultVal instanceof Set) {
            return [key, new Set(queryVal.split("~"))];
          } else if (typeof defaultVal === "number") {
            return [key, Number(queryVal)];
          } else if (typeof defaultVal === "boolean") {
            return [key, Boolean(queryVal)];
          } else {
            return [key, queryVal];
          }
        } else {
          return [key, defaultVal];
        }
      }
    )
  );

  return newFilters;
}

export function filtersToWhere(filters: FiltersState) {
  // let additionalFilters: any = {};
  let completeFilters: { [k: string]: any } = {};

  // if (filters?.rating) {
  //   additionalFilters.rating = filters.rating;
  // }
  // if (filters?.favourite) {
  //   additionalFilters.favourite = filters.favourite;
  // }
  // if (filters?.instructor?.size) {
  //   additionalFilters.instructor = Array.from(filters.instructor);
  // }
  // if (filters?.language?.size) {
  //   additionalFilters.language = Array.from(filters.language);
  // }

  // completeFilters.additionalFilters = additionalFilters;

  /*
  if (filters?.duration.size) {
    completeFilters.duration = Array.from(filters.duration);
  }
  */

  if (filters?.instructor?.size) {
    completeFilters.profiles = { slug: Array.from(filters.instructor) };
  }

  if (filters?.paid) {
    completeFilters.paid = filters.paid;
  }
  if (filters?.subscription) {
    completeFilters.subscription = filters.subscription;
  }
  if (filters?.subscription_allowed) {
    completeFilters.subscription_allowed = "full";
  }
  if (filters?.focusType?.size) {
    completeFilters.practice_focuses = {
      title: Array.from(filters.focusType),
    };
  }
  if (filters?.difficulty?.size) {
    completeFilters.difficulty = Array.from(filters.difficulty);
  }
  if (filters?.skills?.size) {
    completeFilters.practice_skills = {
      title: Array.from(filters.skills),
    };
  }
  if (filters?.bodyPart?.size) {
    completeFilters.practice_body_parts = {
      title: Array.from(filters.bodyPart),
    };
  }
  if (filters?.equipment?.size) {
    completeFilters.practice_equipments = {
      title: Array.from(filters.equipment),
    };
  }
  if (filters?.styleType?.size) {
    completeFilters.practice_styles = {
      title: Array.from(filters.styleType),
    };
  }

  return completeFilters;
}

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  filtersState: DynamicValue<FiltersState>;
  openFilters: DynamicValue<boolean>;
};

function Filter1({ style, className, openFilters, filtersState }: Props) {
  let foundAnyFilters = false;
  let filtersCount = 0;
  const profilesData = useQuery(profileNames, {});

  const profilesByName: {
    [slug: string]: string;
  } = (profilesData?.data?.profiles || []).reduce((acc, profile) => {
    acc[profile.slug] = profile.nickname;
    return acc;
  }, {});

  const { locale: lang } = useRouter();

  const renderedFilters = (
    <div style={{ display: `flex` }}>
      {Object.entries(filtersState.value).map(([filterKey, filterValue]) => {
        if (filterKey === "page") {
          return null;
        }
        if (filterKey === "sort") {
          return null;
        }
        if (filterValue instanceof Set) {
          return (
            <div key={filterKey}>
              {Array.from(filterValue).map((item) => {
                foundAnyFilters = true;
                filtersCount++;
                return (
                  <Badge
                    key={item}
                    pill={true}
                    variant="primary"
                    className={composeClassNames([sBadge, "badge-cancel"])}
                    onClick={() => {
                      const newFilterValue = new Set(filterValue);
                      newFilterValue.delete(item);
                      filtersState.change({
                        ...filtersState.value,
                        [filterKey]: newFilterValue,
                        ["page"]: 1,
                      });
                    }}
                  >
                    {filterKey === "instructor"
                      ? profilesByName[item] || item
                      : getFilterSelectorTranslation(item, lang)}
                    <Small_cancel
                      className={composeClassNames(["badge-cancel-icon"])}
                    />
                  </Badge>
                );
              })}
            </div>
          );
        } else if (typeof filterValue === "boolean") {
          if (filterValue) {
            foundAnyFilters = true;
            filtersCount++;
            return (
              <Badge
                key={filterKey}
                pill={true}
                variant="primary"
                className={composeClassNames([sBadge, "badge-cancel"])}
                onClick={() => {
                  filtersState.change({
                    ...filtersState.value,
                    [filterKey]: false,
                    ["page"]: 1,
                  });
                }}
              >
                {filterKey === "subscription_allowed" ||
                filterKey === "subscription" ? (
                  <>
                    {filterKey === "subscription" && (
                      <Translate tKey="Full subscription" />
                    )}
                    {filterKey === "subscription_allowed" && (
                      <Translate tKey="Subscription trial" />
                    )}
                  </>
                ) : (
                  <Translate tKey={filterKey}></Translate>
                )}
                <Small_cancel
                  className={composeClassNames(["badge-cancel-icon"])}
                />
              </Badge>
            );
          }
          return null;
        } else if (typeof filterValue === "number") {
          if (filterValue) {
            foundAnyFilters = true;
            filtersCount++;
            return (
              <Badge
                key={filterKey}
                pill={true}
                variant="primary"
                className={composeClassNames([sBadge, "badge-cancel"])}
                onClick={() => {
                  filtersState.change({
                    ...filtersState.value,
                    [filterKey]: 0,
                    ["page"]: 1,
                  });
                }}
              >
                <Translate tKey={filterKey}></Translate> = {filterValue}
                <Small_cancel
                  className={composeClassNames(["badge-cancel-icon"])}
                />
              </Badge>
            );
          }
          return null;
        } else {
          return "not supported yet";
        }
      })}
      {filtersCount > 1 && (
        <Badge
          pill={true}
          className={composeClassNames([sBadge, "badge-clear-filter"])}
          onClick={() => {
            filtersState.change(defaultFilter);
          }}
        >
          <Cancel size={constants.Icon.sm} />
        </Badge>
      )}
    </div>
  );

  return (
    <div
      className={composeClassNames([sFilter, className, "filter"])}
      style={style}
    >
      <div className={composeClassNames(["filter-headline", "mb-2"])}>
        <Button
          variant="outline-dark"
          type="button"
          className={composeClassNames([
            sButton,
            "btn-icon-before",
            "filter-add",
            "mr-2",
          ])}
          onClick={() => openFilters.change(!openFilters.value)}
        >
          <Filter size={constants.Icon.md} />
          <Translate tKey="Filter" />
        </Button>
      </div>
      <div className={"filter-badges"}>
        {foundAnyFilters ? (
          renderedFilters
        ) : (
          <div className={composeClassNames(["filter-empty", ,])}>
            <Translate tKey="No filter applied." />
          </div>
        )}
      </div>
    </div>
  );
}

export default Filter1;
